import { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Building2, Plus, Pencil, Trash2, Loader2, RefreshCw, CheckCircle2, Download } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';
import { EstablishmentFormDialog } from '@/components/schools/EstablishmentFormDialog';
import {
  emptyEstablishmentForm,
  type EstablishmentFormValues,
} from '@/components/schools/establishment-form';
import {
  getEstablishments,
  createEstablishment,
  updateEstablishment,
  deleteEstablishment,
  syncEstablishmentsV2,
  importV1Establishments,
} from '@/api/establishments';
import type { Establishment, CreateEstablishmentData } from '@/types/school';

const CONTRACT_TYPE_LABELS: Record<string, string> = {
  with_contract: 'Sous contrat',
  without_contract: 'Hors contrat',
};

const SOURCE_LABELS: Record<string, { label: string; className: string }> = {
  adm: { label: 'Admin', className: 'bg-gray-100 text-gray-600 border-gray-200' },
  v1: { label: 'V1', className: 'bg-blue-50 text-blue-700 border-blue-200' },
  v2: { label: 'V2', className: 'bg-green-50 text-green-700 border-green-200' },
};

interface EstablishmentsCardProps {
  schoolCode: string;
  isEducationNationale: boolean;
  hasV1: boolean;
  hasV2: boolean;
  /** Importe automatiquement les établissements V1 au premier chargement (workflow passage V2) */
  autoImportV1?: boolean;
  /** Retire la bordure/ombre extérieures (utile quand la carte est imbriquée dans une étape de wizard) */
  borderless?: boolean;
}

export function EstablishmentsCard({
  schoolCode,
  isEducationNationale,
  hasV1,
  hasV2,
  autoImportV1 = false,
  borderless = false,
}: EstablishmentsCardProps) {
  const { hasPermission } = useAuth();
  const [establishments, setEstablishments] = useState<Establishment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Establishment | null>(null);
  const [initialValues, setInitialValues] = useState<EstablishmentFormValues>(emptyEstablishmentForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [deleteTarget, setDeleteTarget] = useState<Establishment | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);

  const [importing, setImporting] = useState(false);
  const autoImportDoneRef = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Import V1 préalable (une seule fois) : idempotent côté serveur,
      // les établissements déjà connus sont ignorés
      if (autoImportV1 && hasV1 && !autoImportDoneRef.current) {
        autoImportDoneRef.current = true;
        try {
          const result = await importV1Establishments(schoolCode);
          if (result.imported > 0) {
            setSyncMessage(result.message);
          }
        } catch {
          setError("Impossible d'importer automatiquement les établissements V1");
        }
      }
      const data = await getEstablishments(schoolCode);
      setEstablishments(data);
    } catch {
      setError('Erreur lors du chargement des établissements');
    } finally {
      setLoading(false);
    }
  }, [schoolCode, autoImportV1, hasV1]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreateDialog = () => {
    setEditing(null);
    setInitialValues(emptyEstablishmentForm);
    setFormError(null);
    setDialogOpen(true);
  };

  const openEditDialog = (establishment: Establishment) => {
    setEditing(establishment);
    setInitialValues({
      name: establishment.name,
      isPrincipal: establishment.isPrincipal,
      rne: establishment.rne || '',
      codeAcademie: establishment.codeAcademie || '',
      address: establishment.address || '',
      addressComplement: establishment.addressComplement || '',
      latitude: establishment.latitude,
      longitude: establishment.longitude,
      contractType: establishment.contractType || '',
    });
    setFormError(null);
    setDialogOpen(true);
  };

  const handleSave = async (values: EstablishmentFormValues) => {
    setSaving(true);
    setFormError(null);
    const payload: CreateEstablishmentData = {
      name: values.name,
      isPrincipal: values.isPrincipal,
      rne: values.rne || null,
      codeAcademie: values.codeAcademie || null,
      address: values.address || null,
      addressComplement: values.addressComplement || null,
      latitude: values.latitude,
      longitude: values.longitude,
      contractType: values.contractType || null,
    };

    try {
      if (editing) {
        await updateEstablishment(schoolCode, editing.id, payload);
      } else {
        await createEstablishment(schoolCode, payload);
      }
      setDialogOpen(false);
      await load();
    } catch (err) {
      const axiosError = err as { response?: { data?: { error?: string } } };
      setFormError(axiosError.response?.data?.error || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteEstablishment(schoolCode, deleteTarget.id);
      setDeleteTarget(null);
      await load();
    } catch (err) {
      const axiosError = err as { response?: { data?: { error?: string } } };
      setError(axiosError.response?.data?.error || 'Erreur lors de la suppression');
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  const handleImportV1 = async () => {
    setImporting(true);
    setSyncMessage(null);
    setError(null);
    try {
      const result = await importV1Establishments(schoolCode);
      setSyncMessage(result.message);
      await load();
    } catch (err) {
      const axiosError = err as { response?: { data?: { error?: string } } };
      setError(axiosError.response?.data?.error || 'Erreur lors de l\'import V1');
    } finally {
      setImporting(false);
    }
  };

  const handleSyncV2 = async () => {
    setSyncing(true);
    setSyncMessage(null);
    setError(null);
    try {
      const result = await syncEstablishmentsV2(schoolCode);
      setSyncMessage(result.message);
      await load();
    } catch (err) {
      const axiosError = err as { response?: { data?: { error?: string } } };
      setError(axiosError.response?.data?.error || 'Erreur lors de la synchronisation V2');
    } finally {
      setSyncing(false);
    }
  };

  return (
    <Card className={borderless ? 'border-none shadow-none' : undefined}>
      <CardHeader
        icon={<Building2 />}
        iconColor="blue"
        actions={
          <div className="flex items-center gap-2">
            {hasV1 && hasPermission('action.establishments.create') && (
              <Button size="sm" variant="outline" onClick={handleImportV1} disabled={importing}>
                {importing ? (
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                ) : (
                  <Download className="h-4 w-4 mr-1" />
                )}
                Importer depuis V1
              </Button>
            )}
            {hasV2 && hasPermission('action.establishments.sync') && (
              <Button size="sm" variant="outline" onClick={handleSyncV2} disabled={syncing}>
                {syncing ? (
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-1" />
                )}
                Synchroniser avec V2
              </Button>
            )}
            {hasPermission('action.establishments.create') && (
              <Button size="sm" onClick={openCreateDialog}>
                <Plus className="h-4 w-4 mr-1" />
                Ajouter
              </Button>
            )}
          </div>
        }
      >
        <CardTitle>Établissements</CardTitle>
      </CardHeader>
      <CardContent>
        {error && (
          <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700 mb-4">
            {error}
          </div>
        )}
        {syncMessage && (
          <div className="rounded-lg bg-green-50 border border-green-200 p-3 text-sm text-green-700 mb-4">
            {syncMessage}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : establishments.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
            <Building2 className="h-10 w-10 mb-3 opacity-40" />
            <p className="text-sm">Aucun établissement</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nom</TableHead>
                {isEducationNationale && <TableHead>RNE</TableHead>}
                {isEducationNationale && <TableHead>Académie</TableHead>}
                <TableHead>Type</TableHead>
                <TableHead>Adresse</TableHead>
                <TableHead>Origine</TableHead>
                {hasV2 && <TableHead>Sync V2</TableHead>}
                <TableHead className="w-24" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {establishments.map((establishment) => (
                <TableRow key={establishment.id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{establishment.name}</span>
                      {establishment.isPrincipal && (
                        <Badge variant="secondary" className="text-xs">
                          Principal
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  {isEducationNationale && (
                    <TableCell className="font-mono text-sm">
                      {establishment.rne || <span className="text-muted-foreground">—</span>}
                    </TableCell>
                  )}
                  {isEducationNationale && (
                    <TableCell className="text-sm">
                      {establishment.codeAcademie || <span className="text-muted-foreground">—</span>}
                    </TableCell>
                  )}
                  <TableCell className="text-sm">
                    {establishment.contractType ? (
                      CONTRACT_TYPE_LABELS[establishment.contractType]
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm max-w-56 truncate">
                    {establishment.address || <span className="text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell>
                    <span
                      className={cn(
                        'inline-flex items-center rounded-md border px-1.5 py-0.5 text-[10px] font-medium',
                        (SOURCE_LABELS[establishment.source] || SOURCE_LABELS.adm).className
                      )}
                    >
                      {(SOURCE_LABELS[establishment.source] || SOURCE_LABELS.adm).label}
                    </span>
                  </TableCell>
                  {hasV2 && (
                    <TableCell>
                      {establishment.v2SyncedAt ? (
                        <span className="inline-flex items-center gap-1 text-xs text-green-600">
                          <CheckCircle2 className="h-3.5 w-3.5" />
                          {new Date(establishment.v2SyncedAt).toLocaleDateString('fr-FR')}
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">Non synchronisé</span>
                      )}
                    </TableCell>
                  )}
                  <TableCell>
                    <div className="flex items-center gap-1 justify-end">
                      {hasPermission('action.establishments.edit') && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openEditDialog(establishment)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                      )}
                      {hasPermission('action.establishments.delete') && (
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={establishment.isPrincipal}
                          title={
                            establishment.isPrincipal
                              ? "L'établissement principal ne peut pas être supprimé"
                              : undefined
                          }
                          onClick={() => setDeleteTarget(establishment)}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      {/* Dialog création / édition */}
      <EstablishmentFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        initialValues={initialValues}
        isEditing={editing !== null}
        isEducationNationale={isEducationNationale}
        principalLocked={editing?.isPrincipal}
        saving={saving}
        externalError={formError}
        onSubmit={handleSave}
      />

      {/* Confirmation de suppression */}
      <AlertDialog open={deleteTarget !== null} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer l'établissement ?</AlertDialogTitle>
            <AlertDialogDescription>
              L'établissement « {deleteTarget?.name} » sera supprimé. Cette action est réversible
              par l'équipe technique uniquement.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
